const SOLO_PLAN_CODE = 'BASIC';
const LEGACY_PLUGIN_SOLO_PLAN_CODES = new Set(['PLUGIN_PLAN', 'PLUGIN_FREE']);
export const TEAM_1499_MEMBER_LIMIT = 10;
export const TEAM_2999_MEMBER_LIMIT = 30;
export const TEAM_PRO_MEMBER_LIMIT = 50;
export const TEAM_9999_MEMBER_LIMIT = 100;

const TEAM_MEMBER_LIMIT_BY_PLAN_CODE: Record<string, InviteMemberLimit> = {
    TEAM_1499: TEAM_1499_MEMBER_LIMIT,
    TEAM_2999: TEAM_2999_MEMBER_LIMIT,
    PRO: TEAM_PRO_MEMBER_LIMIT,
    TEAM_9999: TEAM_9999_MEMBER_LIMIT,
    TEAM_49999: null,
};

const TEAM_PLANS_WITH_HIGHER_SELF_SERVICE_MEMBER_TIER = new Set([
    'PLUGIN_TEAM_99',
    'TEAM_1499',
    'TEAM_2999',
    'PRO',
    'TEAM_9999',
]);

type InviteMemberLimit = number | null;

export type MemberInviteEligibility =
    | 'allowed'
    | 'permission-denied'
    | 'upgrade-required'
    | 'member-limit-reached';

export interface WorkspaceMemberLimitEntitlement {
    configured?: boolean | null;
    unlimited?: boolean | null;
    limitValue?: number | string | null;
    source?: string | null;
    planCode?: string | null;
}

const normalizePlanCode = (planCode?: string | null): string => {
    return (planCode || '').trim().toUpperCase();
};

const normalizePositiveInteger = (value?: number | string | null): number | null => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    const parsedValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
        return null;
    }

    return Math.floor(parsedValue);
};

export const resolveInvitePlanCode = ({
    entitlementPlanCode,
    currentWorkspacePlanCode,
    billingSummaryPlanCode,
    userPlanCode,
}: {
    entitlementPlanCode?: string | null;
    currentWorkspacePlanCode?: string | null;
    billingSummaryPlanCode?: string | null;
    userPlanCode?: string | null;
}): string => {
    return normalizePlanCode(entitlementPlanCode)
        || normalizePlanCode(currentWorkspacePlanCode)
        || normalizePlanCode(billingSummaryPlanCode)
        || normalizePlanCode(userPlanCode);
};

export const isSoloInviteUpgradeRequired = (planCode?: string | null): boolean => {
    const normalizedPlanCode = normalizePlanCode(planCode);
    return normalizedPlanCode === SOLO_PLAN_CODE
        || LEGACY_PLUGIN_SOLO_PLAN_CODES.has(normalizedPlanCode)
        || normalizedPlanCode.startsWith('SOLO_')
        || normalizedPlanCode.startsWith('PLUGIN_SOLO_');
};

export const shouldShowMemberInviteEntry = ({
    hasInvitePermission,
    memberInvitesEnabled,
}: {
    hasInvitePermission: boolean;
    memberInvitesEnabled: boolean;
}): boolean => hasInvitePermission && memberInvitesEnabled;

export const resolveInviteMemberLimit = (planCode?: string | null): InviteMemberLimit => {
    const normalizedPlanCode = normalizePlanCode(planCode);
    if (Object.prototype.hasOwnProperty.call(TEAM_MEMBER_LIMIT_BY_PLAN_CODE, normalizedPlanCode)) {
        return TEAM_MEMBER_LIMIT_BY_PLAN_CODE[normalizedPlanCode];
    }
    if (LEGACY_PLUGIN_SOLO_PLAN_CODES.has(normalizedPlanCode)) {
        return 1;
    }
    // Plugin Team limits are dynamic plan entitlements. Never recreate a stale
    // seat cap in the client when the API did not return the current rule.
    return null;
};

export const resolveInvitePlanName = (planCode?: string | null): 'Solo' | 'Team' | null => {
    const normalizedPlanCode = normalizePlanCode(planCode);
    if (normalizedPlanCode === SOLO_PLAN_CODE
        || LEGACY_PLUGIN_SOLO_PLAN_CODES.has(normalizedPlanCode)
        || normalizedPlanCode.startsWith('SOLO_')
        || normalizedPlanCode.startsWith('PLUGIN_SOLO_')) {
        return 'Solo';
    }
    if (normalizedPlanCode.startsWith('PLUGIN_TEAM_')
        || Object.prototype.hasOwnProperty.call(TEAM_MEMBER_LIMIT_BY_PLAN_CODE, normalizedPlanCode)) {
        return 'Team';
    }
    return null;
};

export const hasHigherSelfServiceMemberTier = (planCode?: string | null): boolean => (
    normalizePlanCode(planCode).startsWith('PLUGIN_TEAM_')
    || TEAM_PLANS_WITH_HIGHER_SELF_SERVICE_MEMBER_TIER.has(normalizePlanCode(planCode))
);

export const resolveEffectiveInviteMemberLimit = ({
    planCode,
    memberLimit,
}: {
    planCode?: string | null;
    memberLimit?: WorkspaceMemberLimitEntitlement | null;
}): InviteMemberLimit => {
    if (memberLimit?.unlimited) {
        return null;
    }

    const entitlementLimit = normalizePositiveInteger(memberLimit?.limitValue);
    if (entitlementLimit !== null) {
        return entitlementLimit;
    }

    return resolveInviteMemberLimit(planCode);
};

export const isMemberLimitReached = ({
    memberCount,
    memberLimit,
}: {
    memberCount: number;
    pendingInviteCount?: number;
    memberLimit?: InviteMemberLimit;
}): boolean => {
    if (!memberLimit) {
        return false;
    }

    return memberCount >= memberLimit;
};

export const resolveMemberInviteEligibility = ({
    hasInvitePermission,
    memberInvitesEnabled,
    planCode,
    memberCount,
    memberLimit,
}: {
    hasInvitePermission: boolean;
    memberInvitesEnabled: boolean;
    planCode?: string | null;
    memberCount: number;
    memberLimit?: InviteMemberLimit;
}): MemberInviteEligibility => {
    if (!shouldShowMemberInviteEntry({
        hasInvitePermission,
        memberInvitesEnabled,
    })) {
        return 'permission-denied';
    }
    if (isSoloInviteUpgradeRequired(planCode)) {
        return 'upgrade-required';
    }
    if (isMemberLimitReached({ memberCount, memberLimit })) {
        return 'member-limit-reached';
    }

    return 'allowed';
};

export const isTeamMemberLimitReached = ({
    planCode,
    memberCount,
}: {
    planCode?: string | null;
    memberCount: number;
    pendingInviteCount?: number;
}): boolean => {
    const memberLimit = resolveInviteMemberLimit(planCode);

    return isMemberLimitReached({
        memberCount,
        memberLimit,
    });
};
